import React, { useState, useCallback, useEffect } from 'react';
import { Copy, CheckCircle2, AlertCircle } from 'lucide-react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import BuyMeACoffee from './coffee'; // <-- import the new component
import NavigationPanel from './NavigationPanel';

const PolicySorter = () => {
  const navigate = useNavigate();
  const [rawPolicy, setRawPolicy] = useState('');
  const [sortedPolicy, setSortedPolicy] = useState('');
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  // Dark mode is true by default
  const [darkMode, setDarkMode] = useState(true);

  // Toggles for each statement field
  const [sortSid, setSortSid] = useState(false);
  const [sortAction, setSortAction] = useState(true);
  const [sortNotAction, setSortNotAction] = useState(true);
  const [sortResource, setSortResource] = useState(false);
  const [sortNotResource, setSortNotResource] = useState(false);
  const [sortPrincipal, setSortPrincipal] = useState(false);
  const [sortNotPrincipal, setSortNotPrincipal] = useState(false);
  const [sortCondition, setSortCondition] = useState(false);

  /** Helper: sort an array alphabetically */
  const sortArray = (arr) => [...arr].sort();

  /** Helper: sort keys of an object (shallow) */
  const sortObjectKeys = (obj) => {
    const sortedKeys = Object.keys(obj).sort();
    const newObj = {};
    for (const k of sortedKeys) {
      newObj[k] = obj[k];
    }
    return newObj;
  };

  /** Sort the statement fields conditionally based on toggles */
  const sortStatementFields = (stmt) => {
    const newStmt = { ...stmt };

    if (sortAction && Array.isArray(newStmt.Action)) {
      newStmt.Action = sortArray(newStmt.Action);
    }
    if (sortNotAction && Array.isArray(newStmt.NotAction)) {
      newStmt.NotAction = sortArray(newStmt.NotAction);
    }
    if (sortResource && newStmt.Resource) {
      if (Array.isArray(newStmt.Resource)) {
        newStmt.Resource = sortArray(newStmt.Resource);
      }
    }
    if (sortNotResource && newStmt.NotResource) {
      if (Array.isArray(newStmt.NotResource)) {
        newStmt.NotResource = sortArray(newStmt.NotResource);
      }
    }
    if (sortPrincipal && newStmt.Principal) {
      if (Array.isArray(newStmt.Principal)) {
        newStmt.Principal = sortArray(newStmt.Principal);
      } else if (typeof newStmt.Principal === 'object') {
        newStmt.Principal = sortObjectKeys(newStmt.Principal);
      }
    }
    if (sortNotPrincipal && newStmt.NotPrincipal) {
      if (Array.isArray(newStmt.NotPrincipal)) {
        newStmt.NotPrincipal = sortArray(newStmt.NotPrincipal);
      } else if (typeof newStmt.NotPrincipal === 'object') {
        newStmt.NotPrincipal = sortObjectKeys(newStmt.NotPrincipal);
      }
    }
    if (sortCondition && newStmt.Condition && typeof newStmt.Condition === 'object') {
      newStmt.Condition = sortObjectKeys(newStmt.Condition);
    }

    return newStmt;
  };

  /** Main function that processes the entire policy */
  const processPolicy = (policyString) => {
    const policyObj = JSON.parse(policyString);

    let newStatement;
    if (Array.isArray(policyObj.Statement)) {
      newStatement = policyObj.Statement.map((stmt) => sortStatementFields(stmt));
      if (sortSid) {
        newStatement = newStatement.sort((a, b) => {
          const sidA = a.Sid || '';
          const sidB = b.Sid || '';
          return sidA.localeCompare(sidB);
        });
      }
    } else if (typeof policyObj.Statement === 'object') {
      // Single statement object
      newStatement = sortStatementFields(policyObj.Statement);
    } else {
      // If Statement is neither array nor object, leave it as-is
      newStatement = policyObj.Statement;
    }

    const newPolicy = {
      ...policyObj,
      Statement: newStatement,
    };

    return JSON.stringify(newPolicy, null, 2);
  };

  const handlePolicyInput = (e) => {
    setRawPolicy(e.target.value);
  };

  useEffect(() => {
    if (!rawPolicy.trim()) {
      setSortedPolicy('');
      setError('');
      return;
    }

    setIsProcessing(true);
    try {
      const result = processPolicy(rawPolicy);
      setSortedPolicy(result);
      setError('');
    } catch (err) {
      setError('Invalid JSON policy format');
      setSortedPolicy('');
    }
    setIsProcessing(false);
  }, [
    rawPolicy,
    sortSid,
    sortAction,
    sortNotAction,
    sortResource,
    sortNotResource,
    sortPrincipal,
    sortNotPrincipal,
    sortCondition,
  ]);

  const copyToClipboard = useCallback(() => {
    if (!sortedPolicy) return;
    try {
      const textarea = document.createElement('textarea');
      textarea.value = sortedPolicy;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      textarea.style.top = '50%';
      textarea.style.left = '50%';
      document.body.appendChild(textarea);

      textarea.focus();
      textarea.select();

      const successful = document.execCommand('copy');
      document.body.removeChild(textarea);

      if (successful) {
        setCopied(true);
        setError('');
        setTimeout(() => setCopied(false), 2000);
      } else {
        setError('Failed to copy - please try selecting and copying manually');
      }
    } catch (err) {
      setError('Failed to copy - please try selecting and copying manually');
      console.error('Copy failed:', err);
    }
  }, [sortedPolicy]);

  return (
    <>
      {/* Navigation Bar with color toggle that also changes nav color */}
      <NavigationPanel darkMode={darkMode} setDarkMode={setDarkMode} />

      <div
        className={`
          min-h-screen p-4 md:p-8 transition-colors duration-300
          ${darkMode ? 'bg-gray-900 text-gray-100' : 'bg-gray-100 text-gray-800'}
        `}
      >
        <div className="max-w-7xl mx-auto space-y-6">
          {/* Main Grid: Input Policy + Sorted Policy side by side */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Left Column: Input Policy card */}
            <div
              className={`
                rounded-lg shadow p-6 space-y-4 border border-transparent
                ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-gray-50'}
              `}
            >
              <div className="flex items-center gap-2">
                <label className="text-lg font-semibold">Input Policy</label>
                {isProcessing && (
                  <div className="w-5 h-5 border-2 border-amber-500 rounded-full border-t-transparent animate-spin" />
                )}
              </div>

              <textarea
                onChange={handlePolicyInput}
                placeholder="Paste your Amazon IAM policy here (JSON format)..."
                spellCheck="false"
                className={`
                  w-full h-[500px] p-3 border border-amber-200 rounded-md font-mono text-sm
                  focus:outline-none resize-none transition-colors
                  scrollbar-thin
                  scrollbar-thumb-gray-500
                  scrollbar-track-gray-300
                  hover:scrollbar-thumb-gray-600
                  placeholder-gray-400 dark:placeholder-gray-500
                  ${
                    darkMode
                      ? 'bg-gray-700 border-gray-600 scrollbar-track-gray-700 scrollbar-thumb-gray-500 hover:scrollbar-thumb-gray-400'
                      : 'bg-gray-200 border-gray-300'
                  }
                `}
              />

              {/* Toggles below Input Policy */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-4">
                {/* Sort by Sid */}
                <label className="flex items-center cursor-pointer space-x-2">
                  <span className="text-sm font-medium">Sort Statement ID (Sid)</span>
                  <input
                    type="checkbox"
                    className="sr-only peer"
                    checked={sortSid}
                    onChange={() => setSortSid(!sortSid)}
                  />
                  <div
                    className={`
                      w-11 h-6 rounded-full peer
                      peer-focus:outline-none
                      transition-colors
                      bg-gray-300 dark:bg-gray-600
                      peer-checked:bg-[#FF9900]
                      relative
                      after:content-['']
                      after:absolute
                      after:top-[2px]
                      after:left-[2px]
                      after:bg-white
                      after:border-gray-300
                      after:border
                      after:rounded-full
                      after:h-5
                      after:w-5
                      after:transition-all
                      peer-checked:after:translate-x-full
                      peer-checked:after:border-white
                    `}
                  />
                  <span className="ml-2 text-sm">{sortSid ? 'Yes' : 'No'}</span>
                </label>

                {/* Sort Action (ON by default) */}
                <label className="flex items-center cursor-pointer space-x-2">
                  <span className="text-sm font-medium">Sort Action</span>
                  <input
                    type="checkbox"
                    className="sr-only peer"
                    checked={sortAction}
                    onChange={() => setSortAction(!sortAction)}
                  />
                  <div
                    className={`
                      w-11 h-6 rounded-full peer
                      peer-focus:outline-none
                      transition-colors
                      bg-gray-300 dark:bg-gray-600
                      peer-checked:bg-[#FF9900]
                      relative
                      after:content-['']
                      after:absolute
                      after:top-[2px]
                      after:left-[2px]
                      after:bg-white
                      after:border-gray-300
                      after:border
                      after:rounded-full
                      after:h-5
                      after:w-5
                      after:transition-all
                      peer-checked:after:translate-x-full
                      peer-checked:after:border-white
                    `}
                  />
                  <span className="ml-2 text-sm">{sortAction ? 'Yes' : 'No'}</span>
                </label>

                {/* Sort NotAction (ON by default) */}
                <label className="flex items-center cursor-pointer space-x-2">
                  <span className="text-sm font-medium">Sort NotAction</span>
                  <input
                    type="checkbox"
                    className="sr-only peer"
                    checked={sortNotAction}
                    onChange={() => setSortNotAction(!sortNotAction)}
                  />
                  <div
                    className={`
                      w-11 h-6 rounded-full peer
                      peer-focus:outline-none
                      transition-colors
                      bg-gray-300 dark:bg-gray-600
                      peer-checked:bg-[#FF9900]
                      relative
                      after:content-['']
                      after:absolute
                      after:top-[2px]
                      after:left-[2px]
                      after:bg-white
                      after:border-gray-300
                      after:border
                      after:rounded-full
                      after:h-5
                      after:w-5
                      after:transition-all
                      peer-checked:after:translate-x-full
                      peer-checked:after:border-white
                    `}
                  />
                  <span className="ml-2 text-sm">{sortNotAction ? 'Yes' : 'No'}</span>
                </label>

                {/* Sort Resource */}
                <label className="flex items-center cursor-pointer space-x-2">
                  <span className="text-sm font-medium">Sort Resource</span>
                  <input
                    type="checkbox"
                    className="sr-only peer"
                    checked={sortResource}
                    onChange={() => setSortResource(!sortResource)}
                  />
                  <div
                    className={`
                      w-11 h-6 rounded-full peer
                      transition-colors
                      bg-gray-300 dark:bg-gray-600
                      peer-checked:bg-[#FF9900]
                      relative
                      after:content-['']
                      after:absolute
                      after:top-[2px]
                      after:left-[2px]
                      after:bg-white
                      after:border-gray-300
                      after:border
                      after:rounded-full
                      after:h-5
                      after:w-5
                      after:transition-all
                      peer-checked:after:translate-x-full
                      peer-checked:after:border-white
                    `}
                  />
                  <span className="ml-2 text-sm">{sortResource ? 'Yes' : 'No'}</span>
                </label>

                {/* Sort NotResource */}
                <label className="flex items-center cursor-pointer space-x-2">
                  <span className="text-sm font-medium">Sort NotResource</span>
                  <input
                    type="checkbox"
                    className="sr-only peer"
                    checked={sortNotResource}
                    onChange={() => setSortNotResource(!sortNotResource)}
                  />
                  <div
                    className={`
                      w-11 h-6 rounded-full peer
                      transition-colors
                      bg-gray-300 dark:bg-gray-600
                      peer-checked:bg-[#FF9900]
                      relative
                      after:content-['']
                      after:absolute
                      after:top-[2px]
                      after:left-[2px]
                      after:bg-white
                      after:border-gray-300
                      after:border
                      after:rounded-full
                      after:h-5
                      after:w-5
                      after:transition-all
                      peer-checked:after:translate-x-full
                      peer-checked:after:border-white
                    `}
                  />
                  <span className="ml-2 text-sm">{sortNotResource ? 'Yes' : 'No'}</span>
                </label>

                {/* Sort Principal */}
                <label className="flex items-center cursor-pointer space-x-2">
                  <span className="text-sm font-medium">Sort Principal</span>
                  <input
                    type="checkbox"
                    className="sr-only peer"
                    checked={sortPrincipal}
                    onChange={() => setSortPrincipal(!sortPrincipal)}
                  />
                  <div
                    className={`
                      w-11 h-6 rounded-full peer
                      transition-colors
                      bg-gray-300 dark:bg-gray-600
                      peer-checked:bg-[#FF9900]
                      relative
                      after:content-['']
                      after:absolute
                      after:top-[2px]
                      after:left-[2px]
                      after:bg-white
                      after:border-gray-300
                      after:border
                      after:rounded-full
                      after:h-5
                      after:w-5
                      after:transition-all
                      peer-checked:after:translate-x-full
                      peer-checked:after:border-white
                    `}
                  />
                  <span className="ml-2 text-sm">{sortPrincipal ? 'Yes' : 'No'}</span>
                </label>

                {/* Sort NotPrincipal */}
                <label className="flex items-center cursor-pointer space-x-2">
                  <span className="text-sm font-medium">Sort NotPrincipal</span>
                  <input
                    type="checkbox"
                    className="sr-only peer"
                    checked={sortNotPrincipal}
                    onChange={() => setSortNotPrincipal(!sortNotPrincipal)}
                  />
                  <div
                    className={`
                      w-11 h-6 rounded-full peer
                      transition-colors
                      bg-gray-300 dark:bg-gray-600
                      peer-checked:bg-[#FF9900]
                      relative
                      after:content-['']
                      after:absolute
                      after:top-[2px]
                      after:left-[2px]
                      after:bg-white
                      after:border-gray-300
                      after:border
                      after:rounded-full
                      after:h-5
                      after:w-5
                      after:transition-all
                      peer-checked:after:translate-x-full
                      peer-checked:after:border-white
                    `}
                  />
                  <span className="ml-2 text-sm">{sortNotPrincipal ? 'Yes' : 'No'}</span>
                </label>

                {/* Sort Condition */}
                <label className="flex items-center cursor-pointer space-x-2">
                  <span className="text-sm font-medium">Sort Condition</span>
                  <input
                    type="checkbox"
                    className="sr-only peer"
                    checked={sortCondition}
                    onChange={() => setSortCondition(!sortCondition)}
                  />
                  <div
                    className={`
                      w-11 h-6 rounded-full peer
                      transition-colors
                      bg-gray-300 dark:bg-gray-600
                      peer-checked:bg-[#FF9900]
                      relative
                      after:content-['']
                      after:absolute
                      after:top-[2px]
                      after:left-[2px]
                      after:bg-white
                      after:border-gray-300
                      after:border
                      after:rounded-full
                      after:h-5
                      after:w-5
                      after:transition-all
                      peer-checked:after:translate-x-full
                      peer-checked:after:border-white
                    `}
                  />
                  <span className="ml-2 text-sm">{sortCondition ? 'Yes' : 'No'}</span>
                </label>
              </div>

              {/* Error Message (if any) */}
              {error && (
                <div
                  className="
                    mt-2 flex items-center text-red-600 text-sm bg-red-50 border border-red-200 p-2 rounded-md
                    dark:text-red-400 dark:bg-red-900 dark:border-red-800
                  "
                >
                  <AlertCircle size={18} className="mr-2" />
                  {error}
                </div>
              )}
            </div>

            {/* Right Column: Sorted Policy card */}
            <div
              className={`
                rounded-lg shadow p-6 space-y-4 border border-transparent
                ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-gray-50'}
              `}
            >
              <div className="flex items-center justify-between">
                <label className="text-lg font-semibold">Sorted Policy</label>
                <button
                  onClick={copyToClipboard}
                  className="
                    inline-flex items-center gap-2 px-3 py-1.5 text-sm rounded-md transition-colors
                    bg-[#FF9900] text-white hover:bg-[#FFB84D]
                  "
                >
                  {copied ? (
                    <>
                      <CheckCircle2 size={16} />
                      Copied!
                    </>
                  ) : (
                    <>
                      <Copy size={16} />
                      Copy Sorted Policy
                    </>
                  )}
                </button>
              </div>

              <textarea
                readOnly
                value={sortedPolicy}
                placeholder="Sorted policy will appear here..."
                className={`
                  w-full h-[500px] p-3 border border-amber-200 rounded-md font-mono text-sm
                  focus:outline-none resize-none transition-colors
                  scrollbar-thin
                  scrollbar-thumb-gray-500
                  scrollbar-track-gray-300
                  hover:scrollbar-thumb-gray-600
                  placeholder-gray-400 dark:placeholder-gray-500
                  ${
                    darkMode
                      ? 'bg-gray-700 border-gray-600 scrollbar-track-gray-700 scrollbar-thumb-gray-500 hover:scrollbar-thumb-gray-400'
                      : 'bg-gray-200 border-gray-300'
                  }
                `}
              />

              <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                Tip: You can also manually select the text and press{' '}
                <kbd
                  className="
                    border bg-gray-100 px-1 text-xs font-mono rounded
                    dark:bg-gray-700 dark:border-gray-500
                  "
                >
                  Ctrl + C
                </kbd>{' '}
                to copy.
              </p>

              {/* Tip about toggle switches */}
              <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                Tip: Use the toggle switches (on the left) to enable or disable sorting
                for each field (e.g., “Action”, “Resource”, etc.). Unchecking a toggle
                will leave that field’s order unchanged.
              </p>
            </div>
          </div>
        </div>

        {/* Buy Me A Coffee: Bottom-Right Corner */}
      </div>
      <BuyMeACoffee />
    </>
  );
};

export default PolicySorter;